import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import {
  Users, Mail, Target, TrendingUp, AlertTriangle,
  ShieldCheck, BarChart2, Zap, ArrowUpRight,
} from "lucide-react";
import api from "@/lib/api";

// ── helpers ──────────────────────────────────────────────────────────────
function Stat({ label, value, sub, icon: Icon, accent }) {
  const color = accent === "blue" ? "text-[#002FA7]"
    : accent === "green" ? "text-green-600"
    : accent === "amber" ? "text-amber-600"
    : accent === "red"   ? "text-red-600"
    : "text-[#0A0A0F]";
  return (
    <div className="bg-white border border-[#EAEAE3] rounded-2xl p-5 shadow-soft">
      <div className="flex items-center justify-between mb-3">
        <div className="label-mono">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${color}`} />}
      </div>
      <div className={`font-display text-3xl font-black tracking-tighter ${color}`}>{value}</div>
      {sub && <div className="text-xs text-[#5C5C66] mt-1">{sub}</div>}
    </div>
  );
}

const PLAN_COLOR = {
  trial: "bg-[#F4F4EE] text-[#5C5C66]",
  solo:  "bg-blue-50 text-blue-700",
  pro:   "bg-purple-50 text-purple-700",
  team:  "bg-indigo-50 text-indigo-700",
  scale: "bg-green-50 text-green-700",
};

// Mini bar chart for last-30-day sends
function SendsChart({ data }) {
  if (!data?.length) return <div className="text-xs text-[#5C5C66]">No sending data yet</div>;
  const max = Math.max(...data.map((d) => d.sent), 1);
  return (
    <div className="flex items-end gap-0.5 h-16 w-full">
      {data.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col justify-end group relative">
          <div
            className="bg-[#002FA7] rounded-sm opacity-80 group-hover:opacity-100 transition-opacity"
            style={{ height: `${Math.max(2, (d.sent / max) * 100)}%` }}
          />
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#0A0A0F] text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-10">
            {d.date}: {d.sent}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Access denied or server error"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-[#5C5C66]">
        Loading admin dashboard…
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-16 max-w-xl mx-auto text-center">
        <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="h-6 w-6 text-red-500" />
        </div>
        <h2 className="font-display text-xl font-bold mb-2">Admin access required</h2>
        <p className="text-sm text-[#5C5C66]">{error}</p>
      </div>
    );
  }

  const { users, content, sending } = data;
  const statusColor = sending.today_status === "critical" ? "red"
    : sending.today_status === "warning" ? "amber" : "green";

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2">— Platform Admin</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">
            Admin dashboard
          </h1>
          <p className="text-[#5C5C66] mt-2 text-sm">
            Real-time view of platform health, users, and email infrastructure.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-[#FAFAF7] border border-[#EAEAE3] rounded-xl px-3 py-2 text-xs text-[#5C5C66]">
          <ShieldCheck className="h-3.5 w-3.5 text-[#002FA7]" />
          Admin · {user?.email}
        </div>
      </div>

      {/* ── User stats ── */}
      <div className="label-mono mb-3">Users</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Total users"      value={users.total}             icon={Users}     accent="blue" />
        <Stat label="New today"        value={users.signups_today}     icon={ArrowUpRight} accent="green" />
        <Stat label="New this week"    value={users.signups_week}      icon={TrendingUp} />
        <Stat label="New this month"   value={users.signups_month}     icon={TrendingUp} />
      </div>

      {/* Plan distribution */}
      <div className="bg-white border border-[#EAEAE3] rounded-2xl p-5 shadow-soft mb-8">
        <div className="font-display font-bold mb-4 flex items-center gap-2">
          <Users className="h-4 w-4 text-[#002FA7]" /> Plan distribution
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(users.plan_distribution).map(([plan, count]) => (
            <div key={plan} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-transparent text-sm font-medium ${PLAN_COLOR[plan] || "bg-[#F4F4EE] text-[#5C5C66]"}`}>
              <span className="font-mono uppercase text-xs tracking-wider">{plan}</span>
              <span className="font-bold">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Email infrastructure ── */}
      <div className="label-mono mb-3">Email infrastructure</div>

      {/* Usage bar */}
      <div className={`rounded-2xl p-5 border mb-4 shadow-soft ${
        sending.today_status === "critical" ? "bg-red-50 border-red-200"
        : sending.today_status === "warning"  ? "bg-amber-50 border-amber-200"
        : "bg-white border-[#EAEAE3]"
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold flex items-center gap-2">
            <Zap className={`h-4 w-4 ${sending.today_status === "critical" ? "text-red-600" : sending.today_status === "warning" ? "text-amber-600" : "text-[#002FA7]"}`} />
            Platform sends — today
          </div>
          <span className={`font-mono text-xs font-bold px-2 py-0.5 rounded-md ${
            sending.today_status === "critical" ? "bg-red-100 text-red-700"
            : sending.today_status === "warning"  ? "bg-amber-100 text-amber-700"
            : "bg-green-50 text-green-700"
          }`}>
            {sending.today_pct}%
          </span>
        </div>
        <div className="h-2.5 bg-black/10 rounded-full overflow-hidden mb-3">
          <div
            className={`h-full rounded-full transition-all ${
              sending.today_status === "critical" ? "bg-red-500"
              : sending.today_status === "warning"  ? "bg-amber-500"
              : "bg-[#002FA7]"
            }`}
            style={{ width: `${Math.min(sending.today_pct, 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-[#5C5C66]">
          <span>
            <strong className="text-[#0A0A0F]">{sending.today_count}</strong> sent ·{" "}
            <strong className="text-[#0A0A0F]">{sending.today_limit - sending.today_count}</strong> remaining today ·{" "}
            <strong className="text-[#0A0A0F]">{sending.monthly_total}</strong> this month
          </span>
          <span>Limit: {sending.today_limit}/day</span>
        </div>
        {sending.today_status !== "ok" && (
          <div className={`mt-3 pt-3 border-t flex items-center gap-2 text-xs ${
            sending.today_status === "critical" ? "border-red-200 text-red-800" : "border-amber-200 text-amber-800"
          }`}>
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {sending.today_status === "critical"
              ? "Critical: upgrade Brevo plan immediately to avoid failed deliveries."
              : "Warning: 75% of daily limit used — consider upgrading before end of day."}
            {" "}<a href="https://app.brevo.com/subscription/list" target="_blank" rel="noreferrer" className="underline font-semibold">Upgrade →</a>
          </div>
        )}
      </div>

      {/* 30-day sends chart */}
      <div className="bg-white border border-[#EAEAE3] rounded-2xl p-5 shadow-soft mb-8">
        <div className="font-display font-bold mb-4 flex items-center gap-2">
          <BarChart2 className="h-4 w-4 text-[#002FA7]" /> Sends — last 30 days
        </div>
        <SendsChart data={sending.chart} />
        {sending.chart?.length > 0 && (
          <div className="flex items-center justify-between text-[10px] text-[#9A9AA3] mt-2">
            <span>{sending.chart[0]?.date}</span>
            <span>{sending.chart[sending.chart.length - 1]?.date}</span>
          </div>
        )}
      </div>

      {/* ── Platform content stats ── */}
      <div className="label-mono mb-3">Platform content</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Campaigns"      value={content.campaigns}     icon={Target} />
        <Stat label="Total leads"    value={content.leads}         icon={Users} />
        <Stat label="Emails drafted" value={content.emails_drafted} icon={Mail} />
        <Stat label="Emails sent"    value={content.emails_sent}   icon={Mail} accent="blue" />
        <Stat label="Opened"         value={content.emails_opened}  sub={`${content.open_rate}% open rate`}  accent="green" />
        <Stat label="Clicked"        value={content.emails_clicked} icon={TrendingUp} />
        <Stat label="Replied"        value={content.emails_replied} sub={`${content.reply_rate}% reply rate`} accent="green" />
      </div>

      {/* ── Recent signups ── */}
      <div className="label-mono mb-3">Recent signups</div>
      <div className="bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#EAEAE3]">
              <th className="px-5 py-3 text-left label-mono font-normal">Name</th>
              <th className="px-5 py-3 text-left label-mono font-normal">Email</th>
              <th className="px-5 py-3 text-left label-mono font-normal">Company</th>
              <th className="px-5 py-3 text-left label-mono font-normal">Plan</th>
              <th className="px-5 py-3 text-left label-mono font-normal">Signed up</th>
            </tr>
          </thead>
          <tbody>
            {users.recent.map((u) => (
              <tr key={u.id} className="border-b border-[#EAEAE3] last:border-0 hover:bg-[#FAFAF7] transition-colors">
                <td className="px-5 py-3 font-medium">{u.name}</td>
                <td className="px-5 py-3 text-[#5C5C66] font-mono text-xs">{u.email}</td>
                <td className="px-5 py-3 text-[#5C5C66]">{u.company || "—"}</td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider font-semibold ${PLAN_COLOR[u.plan] || "bg-[#F4F4EE] text-[#5C5C66]"}`}>
                    {u.plan || "trial"}
                  </span>
                </td>
                <td className="px-5 py-3 text-[#5C5C66] text-xs">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
            {users.recent.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-[#5C5C66] text-sm">No users yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
